import { normalizarTexto } from '../lib/textoBR'
import type { RegimeTributario } from '../db/tipos'

export const CAMPOS_PRODUTOS = [
  'codigo', 'descricao', 'preco', 'comissao', 'unidade', 'categoria',
  'linha', 'fabricante', 'foto_url', 'estoque', 'observacoes',
  'quantidade_minima', 'quantidade_maxima',
  'unidades_por_embalagem', 'preco_unidade',
  'ncm', 'ipi_pct', 'icms_pct', 'st_pct', 'pis_cofins_pct',
] as const

export const CAMPOS_CLIENTES = [
  'razao_social', 'nome_fantasia', 'cnpj_cpf', 'ddd', 'telefone', 'email',
  'endereco', 'bairro', 'municipio', 'uf', 'cep', 'regiao', 'segmento',
  'inscricao_estadual', 'regime_tributario', 'linha_credito',
  'codigo_na_representada',
] as const

// A planilha canonica de aliquotas (modelo-aliquotas.xlsx) tem 3 abas, e cada
// uma usa so um subconjunto destes campos -- ICMS: codigo_produto, uf_destino,
// aliquota_pct; ST: codigo_produto, uf_destino, mva_pct; DESCONTO_FRETE:
// regiao_destino, incoterm, desconto_pct. Todos aparecem juntos aqui, como a
// lista de opcoes do <select> de mapeamento: quem esta mapeando so escolhe os
// 3 que valem para a aba que abriu, os outros ficam sem uso nesta importacao.
export const CAMPOS_ALIQUOTAS = [
  'codigo_produto', 'uf_destino', 'aliquota_pct', 'mva_pct',
  'regiao_destino', 'incoterm', 'desconto_pct',
] as const

// O nome que a tela mostra para cada campo mapeavel.
//
// Ate 16/08/2026 a tela mostrava a CHAVE crua (`razao_social`, `cnpj_cpf`,
// `codigo_na_representada`). Isso custou duas coisas de uma vez, as duas
// achadas por ela no smoke do beta:
//
// 1. O <select> se estica ate caber a opcao mais longa. Com chaves cruas mais
//    o aviso de campo tomado, a opcao mais longa passava dos 300px, a caixa
//    comia a linha inteira do celular, e o NOME DA COLUNA DO ARQUIVO -- que e
//    o que a pessoa precisa ler para decidir -- virava reticencias.
// 2. Ela procurou "codigo do cliente" e concluiu que o campo NAO EXISTIA. Ele
//    existia e funcionava desde sempre; so se chamava `codigo_na_representada`
//    na tela. Campo que a pessoa nao reconhece e campo que nao existe.
export const ROTULOS_CAMPO: Record<string, string> = {
  // produtos
  codigo: 'Código',
  descricao: 'Descrição',
  // "Preço" sozinho virou ambiguo quando a tabela passou a ter dois. A planilha
  // da MAVALERIO tem "PRECO DA CAIXA" e "PRECO UNITARIO" lado a lado, e quem
  // esta mapeando precisa LER o que esta escolhendo.
  preco: 'Preço da embalagem',
  comissao: 'Comissão %',
  unidade: 'Unidade',
  categoria: 'Categoria',
  linha: 'Linha',
  fabricante: 'Fabricante',
  foto_url: 'Foto (URL)',
  estoque: 'Estoque',
  observacoes: 'Observações',
  // Curtos de proposito. O <select> da tela de mapeamento se estica ate caber a
  // opcao mais longa, e em 16/08/2026 ele chegou a comer a linha inteira do
  // celular, escondendo o NOME DA COLUNA DO ARQUIVO -- que e justamente o que a
  // pessoa precisa ler para decidir o que mapear.
  quantidade_minima: 'Quant. mínima',
  quantidade_maxima: 'Quant. máxima',
  // Curtos pela razao de sempre: o <select> se estica ate a opcao mais longa e
  // ja comeu a linha inteira do celular em 16/08.
  unidades_por_embalagem: 'Unid. por embalagem',
  preco_unidade: 'Preço avulso',
  // Fiscais. Os mais curtos do arquivo, pela mesma razao das duas linhas acima:
  // o <select> se estica ate a opcao mais longa e come a linha do celular.
  ncm: 'NCM',
  ipi_pct: 'IPI %',
  icms_pct: 'ICMS %',
  st_pct: 'ST %',
  pis_cofins_pct: 'PIS/COFINS %',
  // clientes
  razao_social: 'Razão social',
  nome_fantasia: 'Nome fantasia',
  cnpj_cpf: 'CNPJ/CPF',
  ddd: 'DDD',
  telefone: 'Telefone',
  email: 'E-mail',
  endereco: 'Endereço',
  bairro: 'Bairro',
  municipio: 'Município',
  uf: 'UF',
  cep: 'CEP',
  regiao: 'Região',
  segmento: 'Segmento',
  inscricao_estadual: 'Inscrição estadual',
  regime_tributario: 'Regime tributário',
  linha_credito: 'Linha de crédito',
  // ⚠ NAO renomear de volta para algo com "representada". O codigo vai para o
  // vinculo cliente x fornecedora (`codigo_cliente_na_representada`), mas quem
  // preenche a planilha chama isso de "codigo do cliente" -- e foi por nao
  // reconhecer o nome tecnico que ela concluiu que o campo nao existia.
  codigo_na_representada: 'Código do cliente',
  // aliquotas (as 3 abas de modelo-aliquotas.xlsx)
  codigo_produto: 'Código do produto',
  uf_destino: 'UF destino',
  aliquota_pct: 'Alíquota ICMS %',
  mva_pct: 'MVA %',
  regiao_destino: 'Região destino',
  incoterm: 'Incoterm',
  desconto_pct: 'Desconto %',
}

// Sinonimos normalizados (sem acento) -> campo destino
const SINONIMOS_PRODUTOS: Record<string, string> = {
  codigo: 'codigo', cod: 'codigo', 'cod produto': 'codigo', ref: 'codigo', referencia: 'codigo', sku: 'codigo',
  descricao: 'descricao', 'descricao do produto': 'descricao', produto: 'descricao', nome: 'descricao', item: 'descricao',
  preco: 'preco', 'preco r': 'preco', valor: 'preco', 'valor unitario': 'preco', 'preco unitario': 'preco', unit: 'preco',
  comissao: 'comissao', comis: 'comissao', 'pct comissao': 'comissao',
  'perc comissao': 'comissao', 'percentual comissao': 'comissao', 'comissao produto': 'comissao',
  unidade: 'unidade', un: 'unidade', und: 'unidade', unid: 'unidade', embalagem: 'unidade',
  categoria: 'categoria', grupo: 'categoria', familia: 'categoria', secao: 'categoria',
  linha: 'linha', colecao: 'linha', marca: 'linha',
  // ⚠ `marca` fica em LINHA de proposito -- ver o teste "MARCA continua indo
  // para linha". Fabricante e outra coisa: dentro de UMA fornecedora convivem
  // varios (MAURI e Q SABOR na MAFFER).
  fab: 'fabricante', fabricante: 'fabricante', fabr: 'fabricante',
  foto: 'foto_url', imagem: 'foto_url', 'url foto': 'foto_url', 'foto url': 'foto_url',
  estoque: 'estoque', saldo: 'estoque', disponivel: 'estoque', qtde: 'estoque', quantidade: 'estoque',
  observacoes: 'observacoes', observacao: 'observacoes', obs: 'observacoes',
  // ⚠ FRASES INTEIRAS, nunca `qtde` nem `quantidade` sozinhos: esses dois ja
  // significam ESTOQUE desde sempre (linha acima), e planilhas antigas dependem
  // disso. O cabecalho real do arquivo da FORTE PAN e "QUANT MIN".
  'quant min': 'quantidade_minima', 'quantidade minima': 'quantidade_minima',
  'qtd minima': 'quantidade_minima', 'qtde minima': 'quantidade_minima',
  'quant minima': 'quantidade_minima', 'quantidade min': 'quantidade_minima',
  minimo: 'quantidade_minima', 'minimo de venda': 'quantidade_minima',
  'quantidade minima de venda': 'quantidade_minima',
  'quant max': 'quantidade_maxima', 'quantidade maxima': 'quantidade_maxima',
  'qtd maxima': 'quantidade_maxima', 'qtde maxima': 'quantidade_maxima',
  'quant maxima': 'quantidade_maxima', 'quantidade max': 'quantidade_maxima',
  maximo: 'quantidade_maxima', 'maximo de venda': 'quantidade_maxima',
  'quantidade maxima de venda': 'quantidade_maxima',
  // ⚠ FRASES INTEIRAS, nunca `qtde` nem `quantidade` sozinhos: esses dois ja
  // significam ESTOQUE desde sempre (linha do `estoque`, acima), e planilhas
  // antigas dependem disso. "qtd cx" e o cabecalho real da MAVALERIO.
  'qtd cx': 'unidades_por_embalagem', 'qtde cx': 'unidades_por_embalagem',
  'qt cx': 'unidades_por_embalagem',
  'unidades por caixa': 'unidades_por_embalagem',
  'un por caixa': 'unidades_por_embalagem',
  'unidades por embalagem': 'unidades_por_embalagem',
  'pecas por caixa': 'unidades_por_embalagem',
  'quantidade por caixa': 'unidades_por_embalagem',
  fator: 'unidades_por_embalagem', multiplo: 'unidades_por_embalagem',
  // O preco da embalagem fechada. `preco unitario` NAO entra aqui -- fica em
  // `preco`, onde sempre esteve.
  'preco da caixa': 'preco', 'preco caixa': 'preco',
  'preco da embalagem': 'preco', 'preco embalagem': 'preco',
  'preco fechado': 'preco',
  // O preco da unidade SOLTA.
  'preco avulso': 'preco_unidade', 'preco da unidade': 'preco_unidade',
  'preco unidade': 'preco_unidade', avulso: 'preco_unidade',
  fracionado: 'preco_unidade',
  ncm: 'ncm', 'codigo ncm': 'ncm', 'ncm sh': 'ncm', nbm: 'ncm',
  ipi: 'ipi_pct', 'aliquota ipi': 'ipi_pct', 'ipi pct': 'ipi_pct',
  'perc ipi': 'ipi_pct', 'percentual ipi': 'ipi_pct',
  icms: 'icms_pct', 'aliquota icms': 'icms_pct', 'icms pct': 'icms_pct',
  'perc icms': 'icms_pct', 'percentual icms': 'icms_pct',
  // ⚠ "st" sozinho parece curto demais para ser seguro, e nao e: o mapeamento
  // casa a celula INTEIRA normalizada, nunca um pedaco dela -- "ESTOQUE" nao
  // "contem" a chave st, ele E a chave "estoque". O teste "ST nao rouba nenhum
  // campo" prende isso, porque a hora de descobrir nao e depois de uma
  // importacao ter mapeado a coluna errada em silencio.
  st: 'st_pct', 'icms st': 'st_pct', 'st icms': 'st_pct',
  'substituicao tributaria': 'st_pct', 'subst tributaria': 'st_pct',
  'aliquota st': 'st_pct', 'perc st': 'st_pct',
  'pis cofins': 'pis_cofins_pct', pis: 'pis_cofins_pct', cofins: 'pis_cofins_pct',
  'aliquota pis cofins': 'pis_cofins_pct', 'perc pis cofins': 'pis_cofins_pct',
  'percentual pis cofins': 'pis_cofins_pct',
}

// Os sinonimos que NOMEIAM a embalagem. Quando duas colunas disputam o MESMO
// campo, quem esta nesta lista ganha, e a generica ("preco", "preco unitario",
// "valor", "unit") fica NAO MAPEADA -- visivel na tela de mapeamento, para a
// pessoa resolver na mao.
//
// ⛔ POR QUE EXISTE, medido em 29/08: `['CODIGO','PRECO UNITARIO','PRECO DA
// CAIXA','QTD CX']` mapeava o UNITARIO como preco da tabela e DESCARTAVA o preco
// da caixa -- ganhava o indice menor, e na planilha do Carlos Vaz o unitario vem
// antes. Com fator 20, a caixa passava a custar o preco de uma unidade: o
// desastre exato que a caixa fechada existe para evitar.
//
// ⚠ NAO E O MESMO que repontar o sinonimo. `preco unitario` -> `preco` continua
// valendo e e deliberado (ver o comentario em SINONIMOS_PRODUTOS): no catalogo
// de UMA coluna de preco, "unitario" e O preco, e mexer nisso quebraria toda
// planilha antiga. O que muda e so quem ganha QUANDO AS DUAS APARECEM JUNTAS.
const SINONIMOS_ESPECIFICOS = new Set([
  'preco da caixa', 'preco caixa',
  'preco da embalagem', 'preco embalagem',
  'preco fechado',
])

const SINONIMOS_CLIENTES: Record<string, string> = {
  'razao social': 'razao_social', razao: 'razao_social', cliente: 'razao_social', nome: 'razao_social', empresa: 'razao_social',
  fantasia: 'nome_fantasia', 'nome fantasia': 'nome_fantasia',
  cnpj: 'cnpj_cpf', cpf: 'cnpj_cpf', 'cnpj cpf': 'cnpj_cpf', documento: 'cnpj_cpf', 'cpf cnpj': 'cnpj_cpf',
  telefone: 'telefone', fone: 'telefone', celular: 'telefone', whatsapp: 'telefone', tel: 'telefone',
  email: 'email', 'e mail': 'email',
  endereco: 'endereco', logradouro: 'endereco', rua: 'endereco',
  bairro: 'bairro',
  municipio: 'municipio', cidade: 'municipio',
  uf: 'uf', estado: 'uf',
  cep: 'cep',
  regiao: 'regiao', rota: 'regiao', zona: 'regiao',
  segmento: 'segmento', ramo: 'segmento', atividade: 'segmento',
  ddd: 'ddd', 'ddd telefone': 'ddd',
  'inscricao estadual': 'inscricao_estadual', ie: 'inscricao_estadual',
  'insc estadual': 'inscricao_estadual', 'inscr estadual': 'inscricao_estadual',
  regime: 'regime_tributario', 'regime tributario': 'regime_tributario', tributacao: 'regime_tributario',
  'linha de credito': 'linha_credito', 'limite de credito': 'linha_credito',
  credito: 'linha_credito', limite: 'linha_credito',
  codigo: 'codigo_na_representada', 'cod cliente': 'codigo_na_representada',
  'codigo cliente': 'codigo_na_representada', 'codigo do cliente': 'codigo_na_representada',
}

// Todo mapa de colunas neste modulo e chaveado pela POSICAO da coluna ("0",
// "1", ...), nunca pelo nome: nome de coluna nao e unico. Com duas colunas de
// mesmo cabecalho, chavear por nome fazia as duas dividirem a mesma entrada --
// na tela um <select> mexia no outro, e na leitura a ultima sobrescrevia a
// primeira. Achado 4 do smoke de 2026-07-27.
export function sugerirMapeamento(
  colunas: string[],
  tipo: 'produtos' | 'clientes',
): Record<string, string> {
  const sinonimos = tipo === 'produtos' ? SINONIMOS_PRODUTOS : SINONIMOS_CLIENTES
  const mapa: Record<string, string> = {}
  const usados = new Set<string>()
  // DUAS PASSADAS, e a ordem delas E a regra: a coluna cujo cabecalho NOMEIA a
  // embalagem escolhe primeiro; so depois as genericas pegam o que sobrou.
  // Dentro de cada passada continua valendo o de sempre -- ganha o indice menor.
  // Toda coluna e considerada em exatamente UMA das duas, entao nada e mapeado
  // duas vezes.
  for (const rodadaDosEspecificos of [true, false]) {
    colunas.forEach((coluna, i) => {
      const chave = normalizarTexto(coluna)
      if (SINONIMOS_ESPECIFICOS.has(chave) !== rodadaDosEspecificos) return
      const campo = sinonimos[chave]
      if (campo && !usados.has(campo)) {
        mapa[String(i)] = campo
        usados.add(campo)
      }
    })
  }
  return mapa
}

// Qual coluna ja ocupa este campo? Devolve o indice dela, ou null se estiver
// livre. Duas colunas no mesmo campo nao tem uso legitimo e o estrago e mudo:
// aplicarMapa varre por ordem e a de indice maior sobrescreve a outra. Achado 7
// do smoke de 2026-07-27 -- "DESCRIÇÃO" e "FAB" ambas em `descricao` fariam a
// descricao do catalogo virar o valor do FAB.
export function donoDoCampo(mapa: Record<string, string>, campo: string): number | null {
  const chave = Object.keys(mapa).find((c) => mapa[c] === campo)
  return chave === undefined ? null : Number(chave)
}

// Mantem so a PRIMEIRA coluna de cada campo. Perfis gravados antes do bloqueio
// do Achado 7 podem trazer colisao; barrar apenas na tela nao limparia o que ja
// esta salvo, e ao recarregar a coluna de baixo voltaria a sobrescrever a de
// cima. Ordena por indice para "primeira" significar a coluna mais a esquerda,
// e nao a ordem em que as chaves cairam no JSON.
function semCampoRepetido(mapa: Record<string, string>): Record<string, string> {
  const limpo: Record<string, string> = {}
  const usados = new Set<string>()
  for (const chave of Object.keys(mapa).sort((a, b) => Number(a) - Number(b))) {
    const campo = mapa[chave]
    if (!usados.has(campo)) {
      limpo[chave] = campo
      usados.add(campo)
    }
  }
  return limpo
}

// Perfil ja gravado no formato novo? Todas as chaves sao indices validos do
// arquivo atual. Cabecalho literalmente chamado "0" e o unico falso positivo
// concebivel, e nele a conversao daria no mesmo.
function ehPorIndice(mapa: Record<string, string>, colunas: string[]): boolean {
  const chaves = Object.keys(mapa)
  return chaves.length > 0 && chaves.every((c) => /^\d+$/.test(c) && Number(c) < colunas.length)
}

// Mapa inicial da tela de importação: prioriza o perfil salvo da representada,
// MAS só quando ele reconhece ao menos uma coluna do arquivo enviado. Se o
// arquivo tem cabeçalhos diferentes dos que o perfil guardou (ex.: o modelo em
// branco vs. o export real), o perfil casaria zero colunas e deixaria tudo em
// "ignorar" — nesse caso cai no auto-mapeamento.
export function mapaInicial(
  perfilMapa: Record<string, string> | undefined,
  colunas: string[],
  tipo: 'produtos' | 'clientes',
): Record<string, string> {
  if (perfilMapa) {
    if (ehPorIndice(perfilMapa, colunas)) return semCampoRepetido(perfilMapa)
    // Perfil no formato antigo (por nome): converte para posicao. O `usados`
    // resolve o cabecalho repetido -- so a PRIMEIRA ocorrencia fica mapeada, em
    // vez de as duas apontarem para o mesmo campo como antes.
    const convertido: Record<string, string> = {}
    const usados = new Set<string>()
    colunas.forEach((coluna, i) => {
      const campo = perfilMapa[coluna]
      if (campo && !usados.has(campo)) {
        convertido[String(i)] = campo
        usados.add(campo)
      }
    })
    if (Object.keys(convertido).length > 0) return convertido
  }
  return sugerirMapeamento(colunas, tipo)
}

const SINONIMOS_REGIME: Record<string, RegimeTributario> = {
  'simples nacional': 'simples_nacional', simples: 'simples_nacional', sn: 'simples_nacional',
  'lucro presumido': 'lucro_presumido', presumido: 'lucro_presumido',
  'lucro real': 'lucro_real', real: 'lucro_real',
  mei: 'mei', microempreendedor: 'mei',
  isento: 'isento', 'nao contribuinte': 'isento',
}

// Texto livre da planilha -> codigo do regime (null se nao reconhecido).
export function normalizarRegime(texto: string): RegimeTributario | null {
  return SINONIMOS_REGIME[normalizarTexto(texto)] ?? null
}
